// ORCA-1962: textos e regras da lista de orçamentos (tabela, cards da Início e celular).
// A API devolve a situação de cada orçamento (ORCA-1963); aqui ela vira texto, cor e ações.

export type ProposalSituationType = 'validade' | 'aprovado_em' | 'proxima_parcela' | 'pago_em' | 'reprovado_em';

export interface ProposalSituation {
    type: ProposalSituationType;
    label: string;
    date: string | null;
    days_left?: number | null;
    installment?: { description: string; amount: number } | null;
    estimated?: boolean;
}

export interface ProposalLastActivity {
    action: string;
    icon?: string | null;
    class?: string | null;
    at: string | null;
}

/** Tom do campo "Validade": laranja até 3 dias, vermelho quando expirou, verde quando aprovado/pago */
export type SituationTone = 'muted' | 'warn' | 'danger' | 'ok';

export interface SituationView {
    /** Rótulo do card ("Validade", "Aprovado em", "Próxima parcela"...) */
    label: string;
    /** Valor do card: só a data ou o prazo ("18/10", "Vence em 6 dias") */
    value: string;
    /** Texto completo da tabela ("Aprovado em 21/10", "Vence em 6 dias") */
    full: string;
    tone: SituationTone;
    icon: string;
}

export const EXPIRING_DAYS = 3;

const TONE_ICONS: Record<SituationTone, string> = {
    muted: 'schedule',
    warn: 'hourglass_bottom',
    danger: 'event_busy',
    ok: 'check_circle',
};

export function shortDate(value: string | Date | null | undefined): string {
    if (!value) {
        return '';
    }
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function validityText(daysLeft: number | null | undefined, date: string | null): string {
    if (daysLeft === null || daysLeft === undefined) {
        return 'Sem validade';
    }
    if (daysLeft < 0) {
        return `Expirou em ${shortDate(date)}`;
    }
    if (daysLeft === 0) {
        return 'Vence hoje';
    }
    if (daysLeft === 1) {
        return 'Vence amanhã';
    }
    return `Vence em ${daysLeft} dias`;
}

/**
 * Campo "Validade / situação" (decisão de 26/10): o rótulo muda com o status.
 * Criado mostra "Ainda não enviado"; enviado/expirado, o prazo da validade.
 */
export function situationView(status: number | undefined, situation: ProposalSituation | null | undefined): SituationView {
    const make = (label: string, value: string, full: string, tone: SituationTone): SituationView =>
        ({ label, value, full, tone, icon: TONE_ICONS[tone] });

    if (!situation) {
        return make('Validade', '—', '—', 'muted');
    }

    if (situation.type === 'validade') {
        if (Number(status) === 1) {
            return make('Validade', 'Ainda não enviado', 'Ainda não enviado', 'muted');
        }
        const days = situation.days_left;
        const text = validityText(days, situation.date);
        const tone: SituationTone = days !== null && days !== undefined && days < 0
            ? 'danger'
            : (days !== null && days !== undefined && days <= EXPIRING_DAYS ? 'warn' : 'muted');
        return make('Validade', text, text, tone);
    }

    const date = shortDate(situation.date);
    const tone: SituationTone = situation.type === 'aprovado_em' || situation.type === 'pago_em' ? 'ok' : 'muted';

    return make(situation.label, date || '—', date ? `${situation.label} ${date}` : situation.label, tone);
}

/** "há 15 min", "há 2 horas", "ontem", "há 3 dias" */
export function relativeTime(value: string | Date | null | undefined, now: Date = new Date()): string {
    if (!value) {
        return '';
    }
    const date = value instanceof Date ? value : new Date(value);
    const diff = Math.max(0, now.getTime() - date.getTime());
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) {
        return 'agora';
    }
    if (minutes < 60) {
        return `há ${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
        return hours === 1 ? 'há 1 hora' : `há ${hours} horas`;
    }
    const days = Math.floor(hours / 24);
    if (days === 1) {
        return 'ontem';
    }
    if (days < 7) {
        return `há ${days} dias`;
    }
    const weeks = Math.floor(days / 7);
    if (days < 30) {
        return weeks === 1 ? 'há 1 semana' : `há ${weeks} semanas`;
    }
    const months = Math.floor(days / 30);
    if (months < 12) {
        return months === 1 ? 'há 1 mês' : `há ${months} meses`;
    }
    const years = Math.floor(days / 365);
    return years <= 1 ? 'há 1 ano' : `há ${years} anos`;
}

export function updatedText(value: string | Date | null | undefined): string {
    const relative = relativeTime(value);
    return relative ? `Atualizado ${relative}` : '';
}

/** ORCA-1972: "Criado em 27/10/2026" (data exata; a última atividade mostra o tempo relativo) */
export function createdText(value: string | Date | null | undefined): string {
    const date = value instanceof Date ? value : new Date(value || '');
    return value && !isNaN(date.getTime())
        ? `Criado em ${date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
        : '';
}

/** Última atividade do histórico: "Enviou por e-mail · há 2 horas" */
export function activityText(activity: ProposalLastActivity | null | undefined): string {
    if (!activity?.action) {
        return '';
    }
    const action = activity.action.replace(/\.$/, '');
    const when = relativeTime(activity.at);
    return when ? `${action} · ${when}` : action;
}

export type ProposalQuickAction = 'aprovar' | 'reprovar' | 'enviar' | 'faturar' | 'pagar' | 'recibo' | 'duplicar' | 'renovar';

export interface QuickActionButton {
    action: ProposalQuickAction;
    label: string;
    icon: string;
}

const BUTTONS: Record<ProposalQuickAction, QuickActionButton> = {
    aprovar: { action: 'aprovar', label: 'Aprovar', icon: 'thumb_up' },
    reprovar: { action: 'reprovar', label: 'Reprovar', icon: 'thumb_down' },
    enviar: { action: 'enviar', label: 'Enviar', icon: 'send' },
    faturar: { action: 'faturar', label: 'Faturar', icon: 'receipt_long' },
    pagar: { action: 'pagar', label: 'Registrar pagamento', icon: 'paid' },
    // ORCA-1977: comprovante (documento com ✓); receipt_long já é o Faturar
    recibo: { action: 'recibo', label: 'Recibo', icon: 'task' },
    duplicar: { action: 'duplicar', label: 'Duplicar', icon: 'file_copy' },
    renovar: { action: 'renovar', label: 'Renovar validade', icon: 'event_repeat' },
};

/**
 * Ação visível na linha/card conforme o status (só ícone; as demais ficam no ⋮).
 * Enviado: Aprovar (contorno verde). Os outros status têm uma ação principal.
 */
export function quickAction(status: number | undefined): QuickActionButton | null {
    switch (Number(status)) {
        case 1: return BUTTONS.enviar;
        case 2: return BUTTONS.aprovar;
        case 3: return BUTTONS.faturar;
        case 5: return BUTTONS.pagar;
        case 6: return BUTTONS.recibo;
        case 4: return BUTTONS.duplicar;
        case 7: return BUTTONS.renovar;
        default: return null;
    }
}

export type ProposalStatusAction = 'aprovar' | 'reprovar' | 'faturar' | 'pagar';

const ACTION_RESULT: Record<ProposalStatusAction, { status: number; statusName: string; type: ProposalSituationType; label: string; activity: string }> = {
    aprovar: { status: 3, statusName: 'Aprovado', type: 'aprovado_em', label: 'Aprovado em', activity: 'Proposta aprovada' },
    reprovar: { status: 4, statusName: 'Reprovado', type: 'reprovado_em', label: 'Reprovado em', activity: 'Proposta reprovada' },
    faturar: { status: 5, statusName: 'Faturado', type: 'proxima_parcela', label: 'Próxima parcela', activity: 'Proposta faturada' },
    pagar: { status: 6, statusName: 'Pago', type: 'pago_em', label: 'Pago em', activity: 'Proposta paga' },
};

/**
 * O que muda na linha logo depois de aprovar, reprovar, faturar ou registrar pagamento pela lista
 * (a linha muda na hora, sem recarregar a página).
 */
export function rowAfterAction(action: ProposalStatusAction, date: Date | string | null | undefined, paymentSummary?: string | null): Record<string, any> {
    const result = ACTION_RESULT[action];
    const when = date ? new Date(date) : new Date();
    const now = new Date().toISOString();
    const patch: Record<string, any> = {
        status: result.status,
        statusName: result.statusName,
        canProcess: false,
        situation: { type: result.type, label: result.label, date: when.toISOString() },
        lastActivity: { action: result.activity, at: now },
        updatedAt: now,
    };

    if (paymentSummary) {
        patch['paymentSummary'] = paymentSummary;
    }

    return patch;
}

/** Selo "Pedido de cotação #N" (marcação do sistema: sem cor personalizada) */
export function quoteLabel(cotacaoId: number | null | undefined): string {
    return cotacaoId ? `Pedido de cotação #${cotacaoId}` : '';
}

/** Fundo suave e borda da tag a partir da cor dela (texto sempre escuro para contraste) */
export function tagStyle(color: string | null | undefined): Record<string, string> {
    const hex = /^#[0-9a-f]{6}$/i.test(color || '') ? color as string : '#5ba5fe';
    return {
        'background-color': `${hex}1f`,
        'border-color': `${hex}59`,
    };
}

export function tagDot(color: string | null | undefined): string {
    return /^#[0-9a-f]{6}$/i.test(color || '') ? color as string : '#5ba5fe';
}

/** Mensagem do resultado das ações em massa: "3 orçamentos excluídos; 1 não pôde ser excluído" */
export function bulkResultMessage(succeeded: number, failed: number, done: [string, string], notDone: [string, string]): string {
    const plural = (n: number, forms: [string, string]) => `${n} ${n === 1 ? forms[0] : forms[1]}`;
    const parts: string[] = [];
    if (succeeded > 0) {
        parts.push(plural(succeeded, done));
    }
    if (failed > 0) {
        parts.push(plural(failed, notDone));
    }
    return parts.join('; ') || 'Nenhum orçamento alterado';
}
